import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchMedicalQuestionnaireResumeState,
  resolveNextQuestionOneBased,
} from '../services/medicalQuestionnaire';
import { TEST_HISTORY_PATH } from '../utils/signInRedirect';

function QuestionnaireIllustration() {
  return (
    <div className="relative w-[124px] h-[124px] mx-auto mb-6">
      <div className="absolute inset-[14px] rounded-2xl border border-[#CBD5E0] bg-[#F7F7F7]" />
      <div className="absolute top-[6px] left-1/2 -translate-x-1/2 w-8 h-3 rounded-full border border-[#CBD5E0] bg-[#E8ECEE]" />
      <div className="absolute top-[38px] left-[38px] w-3 h-3 rounded-full border border-[#73C8B0]">
        <div className="absolute left-[2px] top-[2px] w-2 h-[2px] border-b border-l border-[#73C8B0] rotate-[-45deg]" />
      </div>
      <div className="absolute top-[58px] left-[38px] w-3 h-3 rounded-full border border-[#73C8B0]">
        <div className="absolute left-[2px] top-[2px] w-2 h-[2px] border-b border-l border-[#73C8B0] rotate-[-45deg]" />
      </div>
      <div className="absolute top-[78px] left-[38px] w-3 h-3 rounded-full border border-[#73C8B0]">
        <div className="absolute left-[2px] top-[2px] w-2 h-[2px] border-b border-l border-[#73C8B0] rotate-[-45deg]" />
      </div>
      <div className="absolute top-[41px] left-[56px] w-[26px] h-px bg-[#CBD5E0]" />
      <div className="absolute top-[61px] left-[56px] w-[30px] h-px bg-[#CBD5E0]" />
      <div className="absolute top-[81px] left-[56px] w-[22px] h-px bg-[#CBD5E0]" />
      <div className="absolute top-[52px] right-[8px] w-[26px] h-[42px] rounded-[13px] border-[3px] border-[#73C8B0] border-t-transparent border-r-transparent rotate-[24deg]" />
      <div className="absolute top-[88px] right-[6px] w-4 h-4 rounded-full border-[3px] border-[#73C8B0]" />
      <div className="absolute top-[28px] right-[2px] text-[#A7DCCE] text-xl leading-none">+</div>
      <div className="absolute top-[74px] left-[8px] text-[#B8DCD2] text-sm leading-none">+</div>
    </div>
  );
}

export default function MedicalQuestionaingPage() {
  const navigate = useNavigate();
  const questionnaireCompleted = useSelector(
    (s) => s.user.medical_questionnaire_completed === true
  );
  const [isLoadingPrefill, setIsLoadingPrefill] = useState(false);

  useEffect(() => {
    if (questionnaireCompleted) {
      navigate(TEST_HISTORY_PATH, { replace: true });
    }
  }, [questionnaireCompleted, navigate]);

  const handleStart = async () => {
    if (isLoadingPrefill) return;
    try {
      setIsLoadingPrefill(true);
      const { prefillAnswers, numberOfQuestionsFilled } =
        await fetchMedicalQuestionnaireResumeState();
      const nextQuestion = resolveNextQuestionOneBased(numberOfQuestionsFilled, 0);
      navigate('/medical-questioning/questions', {
        state: { prefillAnswers, nextQuestion },
      });
    } catch (_) {
      // If prefill fetch fails, continue with an empty form.
      navigate('/medical-questioning/questions');
    } finally {
      setIsLoadingPrefill(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-180px)] bg-[#F9F5F2] px-4 py-8 md:py-14">
      <div className="mx-auto max-w-[720px] text-center">
        <QuestionnaireIllustration />

        <div className="inline-flex items-center rounded-full bg-[#EEE8FF] text-[#766FB8] text-xs font-poppins font-medium px-4 py-1.5">
          Duur ～ 5 minuten
        </div>

        <h1 className="mt-5 text-[#2B3133] font-libreBaskerville text-[36px] md:text-[42px] leading-[1.15] tracking-[-0.01em] max-w-[680px] mx-auto">
          Vertel ons meer over jouw gezondheid
        </h1>

        <p className="mt-5 text-[#6F7B80] font-poppins text-sm md:text-[15px] leading-relaxed max-w-[680px] mx-auto">
          Beantwoord een aantal vragen over je gezondheid en leefstijl. Zodra jouw
          bloeduitslag binnen is, combineren we deze informatie met je bloedwaarden
          en ontvang je een persoonlijk gezondheidsrapport met inzichten en
          aanbevelingen die passen bij jouw gezondheid en leefstijl.
        </p>

        <div className="mt-6 mx-auto max-w-[680px] rounded-xl border border-[#D1ECE6] bg-[#F0F8F5] px-4 py-3.5 text-left">
          <p className="font-poppins text-sm leading-relaxed text-[#2B3133]">
            <span className="font-semibold text-[#119975]">Let op:</span>{' '}
            Het is belangrijk dat je deze vragenlijst invult in de periode dat je
            de bloedtest afneemt.
          </p>
        </div>

        <button
          type="button"
          onClick={handleStart}
          disabled={isLoadingPrefill}
          className="mt-8 h-12 px-10 rounded-full bg-[#119975] text-white font-poppins text-base font-medium hover:bg-[#0f8a6b] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isLoadingPrefill ? 'Laden...' : 'Start vragenlijst'}
        </button>

        <button
          type="button"
          onClick={() => navigate('/blood-test-summary')}
          className="block mx-auto mt-4 text-[#119975] font-poppins text-sm font-medium hover:underline"
        >
          Voor nu overslaan
        </button>

        <p className="mt-7 text-[#9AA4A9] text-xs font-poppins">
          Jouw gegevens blijven privé.
        </p>
      </div>
    </div>
  );
}
